import { catchError, filter, switchMap } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ActionType, ColumnLike, ColumnType } from '@component/collection-table';
import { ConfirmationModalComponent, ConfirmationModalData } from '@component/confirmation-modal';
import { ContactMember } from '@state/contacts';
import {
  EntitiesService,
  EntityMember,
  EntityMemberCommunicationType,
  entityMemberCommunicationTypeLabels,
  EntityMemberRole,
  entityMemberRoleLabels,
  EntityMemberRoleType
} from '@state/entities';
import { EntityMember as MembershipEntityMember, MemberRoleType } from '@state/memberships';
import { environment } from '@env/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { ID } from '@state/base';
import { ImpersonateResponse, ImpersonationService } from '@state/impersonation/impersonation.service';
import { InvestmentRequestOptions, InvestorService } from '@state/investor';
import { LaunchDarklyRepository } from '@state/launch-darkly';
import { MemberModalComponent, MemberModalData } from '@component/member-modal';
import { noIndent } from '@helpers/string';
import { standardError$ } from '@helpers/error';
import { UserQuery } from '@state/user';

type Member = ContactMember | MembershipEntityMember | EntityMember;
export type MemberLike = ContactMember & MembershipEntityMember & EntityMember;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-team',
  styleUrls: ['./team.component.scss'],
  templateUrl: './team.component.html'
})
export class TeamComponent implements OnInit {
  @Input() actionButtons = true;
  @Input() combineName = false;
  @Input() compact = true;
  @Input() confirmMakePrimaryContact = false;
  @Input() contactId: ID;
  @Input() enableAddMember: boolean;
  @Input() enableCommunicationPreferences = false;
  @Input() enableEdit: boolean;
  @Input({ required: true }) enableMemberSelectRoles: boolean;
  @Input() entityMemberRoles: EntityMemberRoleType[] = [];
  @Input() entityId: string | ID;
  @Input({ required: true }) entityType: string;
  @Input() fundId: string | ID;
  @Input() header: string;
  @Input() investmentParams: InvestmentRequestOptions;
  @Input({ required: true }) members: Member[];
  @Input() noTemplate: boolean;
  @Input() organizationId: ID;
  @Input() outlineButton = false;
  @Input() providerId: ID;
  @Input() subheading: string;
  @Input() title = true;
  @Input() unpadded = false;

  @Output() membersUpdate = new EventEmitter<void>();

  columnSettings: ColumnLike[] = [
    {
      def: 'entity_member_access_role',
      disableSort: true,
      header: 'Roles',
      type: ColumnType.Tags,
      value: (member: MemberLike): string[] =>
        member.entity_member_roles?.map((role) => entityMemberRoleLabels[role.role])
    }
  ];
  membersLoadedFor: ID;

  constructor(
    private cookieService: CookieService,
    private clipboard: Clipboard,
    private dialog: MatDialog,
    private entitiesService: EntitiesService,
    private investorService: InvestorService,
    private impersonationService: ImpersonationService,
    private launchDarklyRepo: LaunchDarklyRepository,
    private snackBar: MatSnackBar,
    private userQuery: UserQuery
  ) {}

  ngOnInit() {
    this.addNameColumnSettings();
    this.addCommunicationPreferencesColumnSettings();
    if (this.actionButtons) this.addActionButtons();
  }

  confirmMakePrimaryContactDialog = (member: MemberLike): void => {
    let data: ConfirmationModalData = {
      infoMessage: noIndent(`
        Changing the primary contact will update the invitation email and any
        uncompleted Sign Subscription task.
      `),
      message: noIndent(`
        The primary contact will be ${member.name} <a href="mailto:${member.email}">${member.email}</a>
      `),
      positiveButtonLabel: 'Make Primary Contact',
      title: 'Make Primary Contact'
    };

    this.dialog
      .open(ConfirmationModalComponent, { data })
      .afterClosed()
      .subscribe({
        next: (confirm: boolean) => {
          if (confirm) this.setMemberRole(member);
        }
      });
  };

  memberHasInvitationToken = (member: MemberLike): boolean => !!member.invitation_token;

  memberInvitationLink = (member: MemberLike): string =>
    `${environment.docsUrl}/invitation/${member.invitation_token}`;

  memberIsPrimaryContact = (member: MemberLike): boolean =>
    member?.entity_member_roles?.some(
      (entityMemberRole: EntityMemberRole) => entityMemberRole.role === EntityMemberRoleType.PrimaryContact
    );

  memberIsNotPrimaryContact = (member: MemberLike): boolean =>
    !member.entity_member_roles.some(
      (entityMemberRole: EntityMemberRole) => entityMemberRole.role === EntityMemberRoleType.PrimaryContact
    );

  memberIsNotOwner = (member: MemberLike): boolean => member.role !== MemberRoleType.Owner;

  memberIsNotViewOnly = (member: MemberLike): boolean => member.role !== MemberRoleType.Viewer;

  removeMember = (member: MemberLike): void => {
    let message = 'Are you sure you want to remove this member?';

    if (member.name && member.name.trim()) {
      message = `Are you sure you want to remove ${member.name.trim()} as a member?`;
    } else if (member.first_name || member.last_name) {
      let name = `${member.first_name} ${member.last_name}`.trim();
      message = `Are you sure you want to remove ${name} as a member?`;
    }

    let data: ConfirmationModalData = { message, positiveButtonLabel: 'Remove', title: 'Remove Member' };

    this.dialog
      .open(ConfirmationModalComponent, { data })
      .afterClosed()
      .pipe(
        filter((confirm: boolean) => confirm),
        switchMap(() =>
          this.entitiesService.removeMember$({
            contact_id: this.contactId,
            entity_id: member.entity_id,
            member_id: member.id,
            provider_id: this.providerId,
            vehicle_id: this.fundId
          })
        ),
        catchError((error: HttpErrorResponse) => standardError$(error))
      )
      .subscribe({
        next: () => {
          this.successMessage('Member Removed');
          this.membersUpdate.emit();
        }
      });
  };

  setMemberRole = (member: MemberLike, roleType?: EntityMemberRoleType): void => {
    let { entityId: entity_id } = this;
    let investment_id: ID;

    if (this.investmentParams) investment_id = this.investmentParams.investment_id;
    this.entitiesService
      .setEntityRoleType$({ entity_id, entity_member_id: member.id, investment_id }, roleType)
      .pipe(catchError((error: HttpErrorResponse) => standardError$(error)))
      .subscribe({ next: () => this.membersUpdate.emit() });
  };

  sendReminder = (member: MemberLike): void => {
    let { contactId: contact_id, providerId: provider_id, fundId: vehicle_id } = this;
    let { entity_id, id: member_id } = member;

    this.entitiesService
      .remindMember$({
        contact_id,
        entity_id,
        member_id,
        provider_id,
        vehicle_id
      })
      .pipe(catchError((error: HttpErrorResponse) => standardError$(error)))
      .subscribe({ next: () => this.successMessage('Reminder Sent') });
  };

  addActionButtons(): void {
    let {
      confirmMakePrimaryContact,
      confirmMakePrimaryContactDialog,
      setMemberRole,
      memberHasInvitationToken,
      memberInvitationLink,
      memberIsNotPrimaryContact,
      memberIsNotOwner,
      memberIsNotViewOnly,
      removeMember,
      sendReminder
    } = this;

    this.columnSettings.push({
      action: {
        condition: (member: MemberLike): boolean =>
          memberHasInvitationToken(member) ||
          (memberIsNotPrimaryContact(member) && memberIsNotViewOnly(member)) ||
          this.enableEdit,
        menu: [
          // This Copy Invite Link is ONLY for LP's members on Investor Slideout
          {
            action: (member: MemberLike) => this.fetchAndCopyMemberInvitationLink(member),
            condition: (): boolean => !!this.investmentParams,
            label: 'Copy Invite Link'
          },
          // This Copy Invite Link is for Entity Team Settings view
          {
            condition: (member: MemberLike): boolean =>
              !this.investmentParams && memberHasInvitationToken(member),
            copyAction: memberInvitationLink,
            label: 'Copy Invite Link',
            successCallback: (isSuccess: boolean) => this.copySuccess(isSuccess)
          },
          {
            action: (member: MemberLike) => this.openMemberModal(true, member),
            condition: (): boolean => this.enableEdit,
            label: 'Edit'
          },
          {
            action: sendReminder,
            condition: (member: MemberLike): boolean => memberHasInvitationToken(member),
            label: 'Send Reminder'
          },
          {
            action: (member: MemberLike) =>
              confirmMakePrimaryContact
                ? confirmMakePrimaryContactDialog(member)
                : setMemberRole(member, EntityMemberRoleType.PrimaryContact),
            condition: (member: MemberLike) =>
              memberIsNotPrimaryContact(member) && memberIsNotViewOnly(member),
            label: 'Make Primary Contact'
          },
          {
            action: removeMember,
            condition: (member: MemberLike): boolean => memberIsNotOwner(member) && this.enableEdit,
            label: 'Remove'
          },
          {
            action: (member: MemberLike) => {
              this.impersonationService
                .impersonate$(member.user_id, this.investmentParams.vehicle_id)
                .subscribe({
                  next: (data: ImpersonateResponse) => {
                    // Store token in cookies. Cookie will be picked up by new apps
                    // Set cookie for all domains that are in the environment.
                    if (!data.jwt) return;

                    let domain = new URL(environment.docsUrl).hostname;

                    this.cookieService.set('IMPERSONATION_TOKEN', data.jwt, {
                      domain,
                      expires: 14,
                      path: '/',
                      sameSite: 'Strict',
                      secure: false
                    });

                    this.cookieService.set('IMPERSONATION_ORIGINAL_URL', window.location.href, {
                      domain,
                      expires: 14,
                      path: '/',
                      sameSite: 'Strict',
                      secure: false
                    });

                    window.location.href = data.redirect_url;
                  }
                });
            },
            class: 'admin-menu-item',
            condition: (): boolean => this.userQuery.isAdmin() && !!this.investmentParams,
            icon: '/assets/icons/lock.svg',
            label: 'Impersonate User'
          }
        ],
        type: ActionType.Menu
      },
      def: 'actions',
      isStickyEnd: true,
      type: ColumnType.Action
    });
  }

  formattedCommunicationTypes(communication_types: EntityMemberCommunicationType[], allify = true): string {
    if (communication_types.length === 0) return null;

    const communicationTypes = Object.values(EntityMemberCommunicationType);

    // Check to see if entity member has all communication types
    if (allify && JSON.stringify(communicationTypes.sort()) === JSON.stringify(communication_types.sort())) {
      return 'All';
    }

    return communication_types.map((key) => entityMemberCommunicationTypeLabels[key]).join(', ');
  }

  addNameColumnSettings(): void {
    if (this.combineName) {
      this.columnSettings.unshift({
        def: 'member',
        disableSort: true,
        header: 'Member',
        type: ColumnType.Meta,
        value: {
          main: 'name',
          meta: (member: EntityMember): string => member.email
        }
      });
    } else {
      this.columnSettings.unshift(
        {
          def: 'name',
          header: 'Name',
          value: 'name'
        },
        {
          def: 'email',
          header: 'Email',
          value: 'email'
        }
      );

      this.columnSettings.splice(-1, 0, {
        def: 'title',
        header: 'Title',
        value: 'title'
      });
    }
  }

  addCommunicationPreferencesColumnSettings(): void {
    // TODO: Remove communicationPreferencesFlagEnabled once feature is released
    if (this.launchDarklyRepo.getEnableCommunicationPreferences() && this.enableCommunicationPreferences) {
      this.columnSettings.push({
        def: 'communication_types',
        disableSort: true,
        header: 'Communications',
        lineClamp: 2,
        type: ColumnType.Tooltip,
        value: {
          main: (member: EntityMember): string =>
            this.formattedCommunicationTypes(member.communication_types),
          tooltip: (member: EntityMember): string =>
            member.communication_types.length
              ? 'This user will receive the following communications: ' +
                this.formattedCommunicationTypes(member.communication_types, false)
              : 'This user will not receive any communications'
        }
      });
    }
  }

  copySuccess(isSuccess: boolean): void {
    if (isSuccess) this.successMessage('Copied!');
  }

  fetchAndCopyMemberInvitationLink(member: MemberLike): void {
    this.investorService
      .fetchInvestorMemberInvitation$({
        entity_member_id: member.id,
        investment_id: this.investmentParams.investment_id,
        vehicle_id: this.fundId
      })
      .subscribe({
        next: (data) => {
          this.clipboard.copy(data.invitation_url);
          this.snackBar.open('Link copied.');
        }
      });
  }

  successMessage(message: string): void {
    this.snackBar.open(message);
  }

  openMemberModal(edit = false, currentMember?: MemberLike): void {
    let {
      contactId,
      entityId,
      enableCommunicationPreferences,
      enableMemberSelectRoles,
      entityType,
      entityMemberRoles,
      fundId,
      noTemplate,
      organizationId,
      providerId
    } = this;
    let primaryContact = this.memberIsPrimaryContact(currentMember);

    let data: MemberModalData = {
      contactId,
      currentMember,
      edit,
      enableCommunicationPreferences,
      enableMemberSelectRoles,
      entityId,
      entityMemberRoles,
      entityType,
      fundId,
      noTemplate,
      organizationId,
      primaryContact,
      providerId
    };

    this.dialog
      .open(MemberModalComponent, { data })
      .afterClosed()
      .subscribe({
        next: (dataRef) => {
          if (dataRef?.reload) this.membersUpdate.emit();
        }
      });
  }
}
